import {
    darken,
    fade,
    lighten,
} from "@material-ui/core/styles/colorManipulator";
import createSpacing from "@material-ui/core/styles/createSpacing";

import { createRelodeTheme, getControlOverrides } from "./";
import { baseColors, spacing } from "./baseColors";
import { createPalette } from "./createPalette";
import { palette } from "./palette";

/* eslint-disable quotes */
const defaultTheme = createRelodeTheme({
    palette: {
        primary: palette.primary,
        secondary: palette.secondary,

        action: {
            active: palette.secondary.main,
            hover: palette.secondary.light,
            hoverOpacity: 0.08,
            selected: palette.secondary.dark,
            disabled: baseColors.inactive_gray,
        },
        error: {
            main: baseColors.danger_red,
        },
        background: {
            default: baseColors.cool_gray,
        },
        common: {
            black: baseColors.black,
        },
    },
    typography: {
        fontFamily: ['"hkgrotesk_regular"', "Helvetica"].join(","),
        fontSize: 16,
        htmlFontSize: 16,
        h1: {
            fontFamily: [
                '"hkgrotesk_bolditalic"',
                "Helvetica",
                "sans-serif",
            ].join(","),
            color: baseColors.navy,
            fontSize: "32px",
            letterSpacing: 0,
            lineHeight: "40px",
            fontWeight: "bold",
            fontStyle: "italic",
        },
        h2: {
            fontFamily: ['"hkgrotesk_bold"', "Helvetica", "sans-serif"].join(
                ","
            ),
            color: baseColors.header_purple,
            fontSize: "24px",
            letterSpacing: 0,
            lineHeight: "30px",
            fontWeight: "bold",
            fontStyle: "normal",
        },
        h3: {
            fontFamily: ['"hkgrotesk_bold"', "Helvetica", "sans-serif"].join(
                ","
            ),
            color: baseColors.header_purple,
            fontSize: "20px",
            letterSpacing: 0,
            lineHeight: "26px",
            fontWeight: "bold",
            fontStyle: "normal",
        },
        h4: {
            fontFamily: [
                '"hkgrotesk_semibold"',
                "Helvetica",
                "sans-serif",
            ].join(","),
            color: baseColors.black,
            fontSize: "20px",
            letterSpacing: 0,
            lineHeight: "30px",
            fontWeight: "bold",
            fontStyle: "normal",
        },
        h5: {
            fontFamily: [
                '"hkgrotesk_semibold"',
                "Helvetica",
                "sans-serif",
            ].join(","),
            color: baseColors.black,
            fontSize: "18px",
            letterSpacing: 0,
            lineHeight: "24px",
            fontWeight: "bold",
            fontStyle: "normal",
        },
        h6: {
            fontFamily: ['"hkgrotesk_regular"', "Helvetica", "sans-serif"].join(
                ","
            ),
            color: baseColors.black,
            fontSize: "18px",
            letterSpacing: 0,
            lineHeight: "24px",
            fontWeight: "normal",
            fontStyle: "normal",
        },
        subtitle2: {
            fontWeight: "bold",
        },
        subtitle1: {
            fontSize: "16px",
            letterSpacing: 0,
            lineHeight: "24px",
        },
        body1: {
            fontFamily: ['"hkgrotesk_regular"', "Helvetica", "sans-serif"].join(
                ","
            ),
            color: baseColors.black,
            fontSize: "16px",
            letterSpacing: 0,
            lineHeight: "24px",
            fontWeight: "normal",
            fontStyle: "normal",
        },
        body2: {
            fontFamily: ['"hkgrotesk_regular"', "Helvetica", "sans-serif"].join(
                ","
            ),
            color: baseColors.black,
            fontSize: "14px",
            letterSpacing: 0,
            lineHeight: "20px",
            fontWeight: "normal",
            fontStyle: "normal",
        },
    },
    spacing: createSpacing(spacing),
    relode: {
        sizes: {
            appBarHeight: spacing * 8,
            drawerWidth: spacing * 20,
            drawerWidthClosed: spacing * 8,
            mobileAppBarHeight: spacing * 8,
            mobileFooterHeight: spacing * 8,
            mobileHeaderHeight: spacing * 8,
            mobileHeaderStatusLabelHeight: spacing * 4,
            mobileTabHeight: spacing * 3,
        },
        colors: {
            alternate: palette.alternate,
            danger: palette.danger,
            negativeAction: createPalette({
                main: palette.danger.main,
                light: lighten(palette.danger.main, 0.7),
                dark: darken(palette.danger.main, 0.2),
            }),
            positiveAction: createPalette({
                main: palette.secondary.main,
                light: lighten(palette.secondary.main, 0.7),
                dark: darken(palette.secondary.main, 0.1),
            }),
            flat: palette.flat,
            backgroundText: createPalette({ main: baseColors.gray }),
            subheadingText: createPalette({ main: baseColors.gray }),
            borderColor: createPalette({ main: baseColors.warm_gray }),
            externalHighlight: createPalette({ main: baseColors.magenta }),
            internalHighlight: palette.secondary,
            appMenuBackground: createPalette({ main: baseColors.warm_gray }),
            rowHighlight: createPalette({ main: baseColors.light_purple }),
            backgroundHighlight: createPalette({ main: baseColors.purple }),
            backgroundHighlightAlt: createPalette({
                main: baseColors.purple_alt,
            }),
            interviewStatus: {
                cancelled: baseColors.inactive_gray,
                scheduled: baseColors.purple,
                completed: baseColors.green,
            },
            candidateStatus: {
                new: createPalette({
                    main: "#A6D3F4",
                    light: fade("#A6D3F4", 0.2),
                    contrastText: "#5BA6DE",
                }),
                qualifying: createPalette({
                    main: "#7E67D8",
                    light: fade("#7E67D8", 0.2),
                }),
                review: createPalette({
                    main: "#5C4FB0",
                    light: fade("#5C4FB0", 0.2),
                }),
                interview: createPalette({
                    main: "#E35AFC",
                    light: fade("#E35AFC", 0.2),
                }),
                rejected: createPalette({
                    main: "#FE6967",
                    light: fade("#FE6967", 0.2),
                }),
                offered: createPalette({
                    main: "#6EEDB8",
                    light: fade("#6EEDB8", 0.2),
                    contrastText: "#1DE294",
                }),
                hired: createPalette({
                    main: "#3F30A3",
                    light: fade("#3F30A3", 0.2),
                }),
                submitted: createPalette({
                    main: "#54D2C7",
                    light: fade("#54D2C7", 0.2),
                }),
                default: createPalette({
                    main: "#FFD467",
                    light: fade("#FFD467", 0.2),
                }),
            },
            pagePrimaryBackground: baseColors.white,
            pageSecondaryBackground: baseColors.cool_gray,
            pageSecondaryColor: baseColors.gray,
            pagePrimaryColor: baseColors.navy,
            inputAreaBackground: baseColors.medium_gray,
            socialShare: {
                email: createPalette({ main: baseColors.share_email }),
                facebook: createPalette({ main: baseColors.share_facebook }),
                linkedin: createPalette({ main: baseColors.share_linkedin }),
                twitter: createPalette({ main: baseColors.share_twitter }),
            },
            chip: {
                matched: createPalette({
                    main: baseColors.navy,
                    contrastText: baseColors.white,
                }),
                unselected: createPalette({
                    main: "#F1EFF5",
                    contrastText: baseColors.black,
                }),
                selected: createPalette({
                    main: baseColors.light_blue,
                    contrastText: baseColors.black,
                }),
                notMatchable: createPalette({
                    main: baseColors.white,
                    contrastText: baseColors.black,
                }),
            },
            label: createPalette({
                main: "#757575",
                dark: "#212121",
                light: "#A8A8A8",
            }),
        },
        controls: {},
    },
});

// enable access to theme for breakpoints, etc. from overrides
export const siteTheme = {
    ...defaultTheme,
    overrides: getControlOverrides(defaultTheme),
};
